/**
 * Fetches the product listing + owns pagination + debounces the price filter.
 *
 * Stays tightly focused on **data**: accepts the current filter state as
 * inputs and returns rows + totals. URL-mirroring lives in
 * `useProductFilters` so this hook can be re-used for SEO landings later.
 *
 * Extracted from `app/(main)/products/ProductsClient.tsx` lines 933–1058 +
 * 991 (the `limit = 24` constant).
 */

"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { Product } from "@/stores/product-store";
import type { DeliveryTiming } from "@/components/products/FilterSections/DeliveryTimingSection";
import type { ProductSort } from "@/components/products/FilterSections/SortSection";

interface ProductsResponse {
  items: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  minPricePence?: number;
  maxPricePence?: number;
}

const DEFAULT_PAGE_SIZE = 24;

/** Stable empty array so an omitted optional facet doesn't change deps identity. */
const EMPTY_LIST: string[] = [];

export interface UseProductsQueryParams {
  categoriesSelected: string[];
  occasionsSelected: string[];
  coloursSelected: string[];
  /** Flower/plant-type facet (`?flowers=`). Sent multi-value to the API. */
  flowersSelected?: string[];
  deliveryTiming: DeliveryTiming;
  minPrice: string;
  maxPrice: string;
  sort: ProductSort;
  /**
   * Active nav-tab slug from `?tab=<slug>`. The server resolves this via
   * `resolveNavTabPredicate` and intersects with the other filters, so
   * combining a tab with e.g. a colour still narrows as you'd expect.
   */
  activeTabSlug?: string | null;
  /**
   * Collection slug that locks every fetch to this collection — even if
   * the user-controlled `categoriesSelected` is empty. Used on
   * `/products/<slug>` pages so the filter sidebar (hidden in that
   * context) can't drift the listing out of the collection. Always
   * appended as a `categorySlug` query param so the server's intersection
   * logic kicks in correctly.
   */
  lockedCategorySlug?: string;
  /** Page size. Defaults to 24. */
  limit?: number;
  /**
   * SSR-seeded page (CRAWL-1). When provided, the grid renders these products
   * server-side (real `<a href="/p/[slug]">` cards, faster LCP, no-JS
   * fallback) and the hook SKIPS its mount fetch.
   *
   * The seed is now built for **any** filter combination and for any
   * `?page=N` — not just the bare `/products` URL — so `seeded` means "the
   * server already rendered the page matching these props", NOT "unfiltered".
   * That is why `initialPage` exists and why the two page-reset effects skip
   * their first run: without that, a seeded `?page=2` would be yanked back to
   * page 1 on mount and immediately refetched. Any later filter/page/sort
   * change still refetches normally. `undefined` = not seeded → fetch on
   * mount as before.
   */
  initialProducts?: Product[];
  initialTotal?: number;
  initialPriceRangePence?: { min: number; max: number } | null;
  /** Page the server seeded (from `?page=`). Defaults to 1. */
  initialPage?: number;
  /**
   * Mirror `page` into the URL as `?page=N` so pagination is a real,
   * shareable, crawlable URL. Defaults to true; pass false for embedded grids
   * that must not touch the address bar.
   */
  syncPageToUrl?: boolean;
}

export interface UseProductsQueryResult {
  products: Product[];
  total: number;
  loading: boolean;
  page: number;
  setPage: (next: number) => void;
  limit: number;
  priceRangePence: { min: number; max: number } | null;
  /** Debounced min/max values the fetcher actually sent to the API. */
  debouncedMinPrice: string;
  debouncedMaxPrice: string;
}

export function useProductsQuery(
  params: UseProductsQueryParams
): UseProductsQueryResult {
  const {
    categoriesSelected,
    occasionsSelected,
    coloursSelected,
    flowersSelected,
    deliveryTiming,
    minPrice,
    maxPrice,
    sort,
    activeTabSlug,
    lockedCategorySlug,
    limit = DEFAULT_PAGE_SIZE,
    initialProducts,
    initialTotal,
    initialPriceRangePence,
    initialPage = 1,
    syncPageToUrl = true,
  } = params;

  const flowers = flowersSelected ?? EMPTY_LIST;
  const seeded = initialProducts !== undefined;
  const [products, setProducts] = useState<Product[]>(initialProducts ?? []);
  const [total, setTotal] = useState(initialTotal ?? 0);
  const [priceRangePence, setPriceRangePence] =
    useState<{ min: number; max: number } | null>(initialPriceRangePence ?? null);
  // When seeded, the first paint already has products — don't flash skeletons.
  const [loading, setLoading] = useState(!seeded);
  // Views start on the page the SERVER rendered (`?page=N`), not always 1.
  const [page, setPageState] = useState(Math.max(1, initialPage));
  // Skip exactly the first (mount) fetch when SSR-seeded; every later
  // filter/page/sort change still fetches via the fetchProducts effect.
  const skipNextFetch = useRef(seeded);
  // Seed the debounced price from the URL-derived value when SSR-seeded: the
  // server already applied it, so starting at "" would fire a redundant
  // unfiltered refetch 500ms after mount.
  const [debouncedMinPrice, setDebouncedMinPrice] = useState(
    seeded ? minPrice : ""
  );
  const [debouncedMaxPrice, setDebouncedMaxPrice] = useState(
    seeded ? maxPrice : ""
  );

  /**
   * Mirror the page into the URL. `replaceState` (not push) deliberately: the
   * paginator's anchors give crawlers + middle-click a real `?page=N` URL,
   * while in-page paging stays a single history entry — a pushState here would
   * add entries the Back button couldn't honour (page lives in React state, so
   * popstate wouldn't restore it).
   */
  const writePageToUrl = useCallback(
    (next: number) => {
      if (!syncPageToUrl || typeof window === "undefined") return;
      const url = new URL(window.location.href);
      if (next <= 1) url.searchParams.delete("page");
      else url.searchParams.set("page", String(next));
      window.history.replaceState(null, "", url.pathname + url.search);
    },
    [syncPageToUrl]
  );

  const setPage = useCallback(
    (next: number) => {
      setPageState(next);
      writePageToUrl(next);
    },
    [writePageToUrl]
  );

  /*
   * Both page resets below compare a SIGNATURE against a ref seeded with the
   * mount value, rather than firing unconditionally on the first effect run.
   *
   * The old unconditional `setPage(1)` was harmless only because `page` always
   * started at 1. Now that a seeded `?page=2` starts at 2, an unconditional
   * mount reset would yank it back to page 1 and refetch — throwing away the
   * server-rendered page the crawler (and the customer) just received. A
   * "skip the first run" boolean isn't enough either: the price effect's
   * `setPage` happens inside a 500ms timeout that a re-render can cancel, so
   * "first run" and "first fire" aren't the same event. Comparing signatures
   * is timing-independent.
   */
  const priceSig = `${minPrice}|${maxPrice}`;
  const lastPriceSig = useRef(priceSig);

  // Debounce price inputs (500ms) — typing in the min/max fields on mobile
  // was triggering fetches on every keystroke, which re-rendered the input
  // and closed the software keyboard.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedMinPrice(minPrice);
      setDebouncedMaxPrice(maxPrice);
      if (lastPriceSig.current !== priceSig) {
        lastPriceSig.current = priceSig;
        setPage(1);
      }
    }, 500);
    return () => window.clearTimeout(t);
  }, [minPrice, maxPrice, priceSig, setPage]);

  // Reset page to 1 whenever the filter set changes (not just price — price
  // is handled by the debounced effect above). Joined into one string so the
  // effect fires on membership change without identity-unstable array deps.
  const filterSig = [
    categoriesSelected.join(","),
    occasionsSelected.join(","),
    coloursSelected.join(","),
    flowers.join(","),
    deliveryTiming,
    sort,
    activeTabSlug ?? "",
    lockedCategorySlug ?? "",
  ].join("|");
  const lastFilterSig = useRef(filterSig);
  useEffect(() => {
    if (lastFilterSig.current === filterSig) return;
    lastFilterSig.current = filterSig;
    setPage(1);
  }, [filterSig, setPage]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      // Wave-2 contract: send slugs via `categorySlug` (multi-value). The
      // backend `/api/products` endpoint resolves slugs → category ids and
      // expands to include children. Legacy name-based filters still work
      // via the `category` param but the filter UI now emits slugs.
      // The locked collection (collection landing page) is ALWAYS applied
      // — it can't be cleared by user state or stale URL params. De-dupe
      // so the user can't accidentally double-send it via the sidebar.
      const slugSet = new Set<string>();
      if (lockedCategorySlug) slugSet.add(lockedCategorySlug);
      for (const c of categoriesSelected) slugSet.add(c);
      slugSet.forEach((c) => query.append("categorySlug", c));
      occasionsSelected.forEach((o) => query.append("occasion", o));
      coloursSelected.forEach((c) => query.append("colour", c));
      flowers.forEach((f) => query.append("flowers", f));
      if (deliveryTiming) query.set("deliveryTiming", deliveryTiming);
      if (debouncedMinPrice) {
        const min = Math.max(0, Math.min(9999, parseInt(debouncedMinPrice, 10) || 0));
        query.set("minPrice", String(min * 100));
      }
      if (debouncedMaxPrice) {
        const max = Math.max(0, Math.min(9999, parseInt(debouncedMaxPrice, 10) || 0));
        query.set("maxPrice", String(max * 100));
      }
      query.set("page", String(page));
      query.set("limit", String(limit));
      if (sort && sort !== "default") query.set("sort", sort);
      if (activeTabSlug) query.set("tab", activeTabSlug);
      const res = await fetch(`/api/products?${query}`);
      if (!res.ok) throw new Error("Failed to load products");
      const data: ProductsResponse = await res.json();
      setProducts(data.items);
      setTotal(data.total);
      if (data.minPricePence != null && data.maxPricePence != null) {
        setPriceRangePence({ min: data.minPricePence, max: data.maxPricePence });
      } else {
        setPriceRangePence(null);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [
    categoriesSelected,
    occasionsSelected,
    coloursSelected,
    flowers,
    deliveryTiming,
    debouncedMinPrice,
    debouncedMaxPrice,
    page,
    limit,
    sort,
    activeTabSlug,
    lockedCategorySlug,
  ]);

  useEffect(() => {
    if (skipNextFetch.current) {
      // First mount with an SSR seed — keep the server-rendered page-1 result
      // instead of immediately refetching the identical data.
      skipNextFetch.current = false;
      return;
    }
    fetchProducts();
  }, [fetchProducts]);

  return {
    products,
    total,
    loading,
    page,
    setPage,
    limit,
    priceRangePence,
    debouncedMinPrice,
    debouncedMaxPrice,
  };
}
